import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Printer, X } from 'lucide-react';
import QRCode from 'qrcode';
import PrintReportShell from '@/components/print/PrintReportShell';
import { accountingApi, type AccountingDocumentRecord } from '@/lib/api/accounting';
import { getAccountingPrintSettings } from '@/lib/printSettings';

const AccountingDocumentPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [doc, setDoc] = React.useState<AccountingDocumentRecord | null>(null);
  const [projectNameMap, setProjectNameMap] = React.useState<Record<number, string>>({});
  const [accountTitleMap, setAccountTitleMap] = React.useState<Record<string, string>>({});
  const [qrSrc, setQrSrc] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [printedAt] = React.useState(() =>
    new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date()),
  );
  const settings = getAccountingPrintSettings();

  React.useEffect(() => {
    if (!id) return;
    setLoading(true);
    accountingApi
      .accountingDocument(id)
      .then(setDoc)
      .catch(() => setDoc(null))
      .finally(() => setLoading(false));
  }, [id]);

  React.useEffect(() => {
    accountingApi
      .lookups()
      .then((data) => {
        const map: Record<number, string> = {};
        (data.projects || []).forEach((p) => {
          map[p.id] = p.label;
        });
        setProjectNameMap(map);
      })
      .catch(() => setProjectNameMap({}));

    accountingApi
      .accountingHeadingsTree()
      .then((nodes) => {
        const map: Record<string, string> = {};
        const walk = (items: typeof nodes) => {
          items.forEach((n) => {
            map[n.code] = n.title;
            if (n.children?.length) walk(n.children);
          });
        };
        walk(nodes);
        setAccountTitleMap(map);
      })
      .catch(() => setAccountTitleMap({}));
  }, []);

  React.useEffect(() => {
    if (!doc) {
      setQrSrc('');
      return;
    }
    const vc = doc.verificationCode?.trim();
    if (!settings.showQr || !vc) {
      setQrSrc('');
      return;
    }
    const payload = JSON.stringify({
      company: settings.companyName,
      docNo: doc.docNo,
      verificationCode: vc,
      verifyEndpoint: `/api/panel/accounting/accounting-documents/verify/${vc}`,
      generatedAt: printedAt,
    });
    QRCode.toDataURL(payload, { width: 90, margin: 1 })
      .then((dataUrl) => setQrSrc(dataUrl))
      .catch(() => setQrSrc(''));
  }, [doc, printedAt, settings.showQr, settings.companyName]);

  if (!id) return <Navigate to="/accounting-documents" replace />;
  if (loading) {
    return <div className="min-h-screen bg-slate-100 p-6 text-sm text-slate-600">در حال آماده‌سازی چاپ...</div>;
  }
  if (!doc) return <Navigate to="/accounting-documents" replace />;
  const hasVerificationCode = Boolean(doc.verificationCode?.trim());

  const toolsPanel = (
    <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
      <div className="text-sm text-slate-700">
        <div className="font-bold">سند حسابداری شماره {doc.docNo}</div>
        <div>تاریخ: {doc.docDate}</div>
      </div>
      <div className="flex gap-2">
        <Link
          to={`/accounting-documents/${doc.id}`}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
        >
          <X className="h-4 w-4" />
          بستن
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700"
        >
          <Printer className="h-4 w-4" />
          چاپ / PDF
        </button>
      </div>
    </div>
  );

  return (
    <PrintReportShell
      pageSize={`A4 ${settings.pageOrientation}`}
      toolsPanel={toolsPanel}
      watermarkText={settings.watermarkEnabled ? `${settings.watermarkText} ${printedAt}` : undefined}
      printFooter={
        <span className="text-[11px] text-slate-700">
          {settings.companyName}
          {' | '}شماره سند: {doc.docNo}
          {hasVerificationCode ? ` | کد رهگیری: ${doc.verificationCode}` : ''}
        </span>
      }
    >
      <div className="mb-3 grid grid-cols-3 rounded-md border-2 border-black p-2">
        <div className="flex items-start justify-start">
          {settings.logoDataUrl ? (
            <img src={settings.logoDataUrl} alt="logo" className="h-14 w-14 object-contain" />
          ) : (
            <div className="h-14 w-14 rounded border text-[10px] text-slate-500 flex items-center justify-center">لوگو</div>
          )}
        </div>
        <div className="text-center">
          <div className="text-sm font-bold">سند حسابداری</div>
          <div className="mt-1 text-xs text-slate-700">شماره سند: {doc.docNo}</div>
          <div className="text-xs text-slate-700">تاریخ سند: {doc.docDate}</div>
        </div>
        <div className="flex items-start justify-end">
          {settings.showQr && hasVerificationCode ? (
            <img src={qrSrc} alt="qr" className="h-14 w-14 border object-contain" />
          ) : null}
        </div>
      </div>

      <div className="mb-2 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded border p-2">
          <span className="font-semibold">نام شرکت:</span> {settings.companyName}
        </div>
        <div className="rounded border p-2 text-left" dir="ltr">
          <span className="font-semibold">Printed At:</span> {printedAt}
        </div>
      </div>
      {hasVerificationCode ? (
        <div className="mb-3 rounded border p-2 text-xs">
          <span className="font-semibold">کد رهگیری/استعلام سند:</span> {doc.verificationCode}
        </div>
      ) : null}

      <div className="mb-3 rounded border p-2 text-xs">
        <span className="font-semibold">شرح عملیات:</span> {doc.operationDescription || '-'}
      </div>

      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-slate-200">
            <th className="border border-slate-500 p-1">ردیف</th>
            <th className="border border-slate-500 p-1">عنوان حساب</th>
            <th className="border border-slate-500 p-1">نام پروژه</th>
            <th className="border border-slate-500 p-1">شرح عنوان</th>
            <th className="border border-slate-500 p-1">بدهکار</th>
            <th className="border border-slate-500 p-1">بستانکار</th>
          </tr>
        </thead>
        <tbody>
          {(doc.rows || []).map((r, i) => (
            <tr key={r.id}>
              <td className="border border-slate-400 p-1 text-center">{i + 1}</td>
              <td className="border border-slate-400 p-1">{accountTitleMap[r.accountCode] || r.accountTitle || '-'}</td>
              <td className="border border-slate-400 p-1">{(r.projectId && projectNameMap[r.projectId]) || '-'}</td>
              <td className="border border-slate-400 p-1">{r.accountTitle || '-'}</td>
              <td className="border border-slate-400 p-1 text-left font-mono" dir="ltr">
                {Number(r.debit || 0).toLocaleString('en-US')}
              </td>
              <td className="border border-slate-400 p-1 text-left font-mono" dir="ltr">
                {Number(r.credit || 0).toLocaleString('en-US')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
        <div className="rounded border p-2">جمع بدهکار: {Number(doc.totalDebit || 0).toLocaleString('en-US')}</div>
        <div className="rounded border p-2">جمع بستانکار: {Number(doc.totalCredit || 0).toLocaleString('en-US')}</div>
        <div className="rounded border p-2">اختلاف: {Math.abs(Number(doc.difference || 0)).toLocaleString('en-US')}</div>
      </div>
    </PrintReportShell>
  );
};

export default AccountingDocumentPrintPage;
